<template>
  <eco-suite-navbar-menu
    v-if="companies.length >= 2"
    slot="utilities"
    icon="mod-company-admin"
    hide-menu-header
    :label="currentCompany?.name"
    :tooltip="trans('Company selector')"
  >
    <form
      ref="$form"
      method="post"
      :action="route('lms.context')"
      style="display: none"
    >
      <input type="hidden" name="_token" :value="csrfToken" />
      <input type="hidden" name="company_id" :value="selectedCompany.id" />
    </form>

    <div v-if="companies.length > 10" class="search-input">
      <pwc-icon-shadow
        icon="search"
        size="sm"
        aria-hidden="true"
      ></pwc-icon-shadow>
      <input
        ref="$search"
        v-model="search.query"
        :placeholder="trans('Search')"
        @keydown.enter.prevent="onSearchSubmit(false)"
      />
    </div>

    <eco-suite-navbar-menu-item
      v-for="company in companiesOptions"
      :key="company.id"
      :is-current="currentCompany.id === company.id ? true : undefined"
      @click="select(company)"
    >
      <div slot="label">
        <div>{{ company.name }}</div>
      </div>
      <div slot="suffix" class="text-sm flex justify-end gap-1">
        <span v-if="company.orgnr">{{ company.orgnr }}</span>
        <span class="flag text-black">{{ flag(company.country) }}</span>
      </div>
    </eco-suite-navbar-menu-item>
  </eco-suite-navbar-menu>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import { trans, flag } from '@/munio/i18n'
import { can } from '@/munio/auth'
import { route } from '@/munio/http'
import { debounce } from '@/munio/utils'
import {
  CompanyBasicResourceDto,
  CompanySearchResultResourceDto,
} from '@/munio/api/data'

type Company = CompanyBasicResourceDto & {
  nameLegal?: string
  nameShort?: string
}

const csrfToken =
  document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ??
  ''

const $form = ref<HTMLFormElement>()
const $search = ref<HTMLInputElement>()
const companies = ref<Company[]>(window.Munio.config.companies as Company[])
const selectedCompany = ref(window.Munio.config.company)
const search = ref({
  query: '',
  results: [] as CompanySearchResultResourceDto[],
  active: false,
  debounce: debounce(onSearchSubmit, 500),
})

const currentCompany = computed(() => {
  return window.Munio.config.company
})

const companiesOptions = computed(() => {
  if (!search.value.query) {
    return companies.value
  }

  return search.value.results
})

function select(company: any) {
  selectedCompany.value = company
  nextTick(() => {
    $form.value?.submit()
  })
}

async function onSearchSubmit(debounced = false) {
  if (search.value.query.length < 3) {
    return
  }

  if (debounced && companiesOptions.value.length) {
    return
  }

  if (can('munio')) {
    try {
      search.value.active = true
      const { data } = await window.Munio.api.companies.search(
        search.value.query,
        undefined,
        undefined,
        true,
      )
      search.value.results = data
    } catch (error) {
      console.error(error)
    } finally {
      search.value.active = false
    }

    return
  }

  const firstCompany = companiesOptions.value[0]

  if (firstCompany) {
    select(firstCompany)
  }
}

watch(
  () => search.value.query,
  () => {
    search.value.results = []
    search.value.debounce(true)
  },
)
</script>

<style scoped>
.search-input {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--pdt-space-200);
  width: 100%;
  margin-top: var(--pdt-space-75);
  margin-bottom: var(--pdt-space-75);
  padding: 0 var(--pdt-space-75);

  pwc-icon-shadow {
    color: var(--pdt-color-text-disabled);
    position: absolute;
    inset-inline-start: var(--pdt-space-200);
    top: var(--pdt-space-75);
  }

  input {
    height: 32px;
    width: 500px;
    outline: 0;
    padding-block: var(--pdt-space-50);
    padding-inline: var(--pdt-space-400) var(--pdt-space-100);
    background-color: var(--pdt-color-control-bg);
    border: 1px solid var(--pdt-color-border);
    border-radius: var(--pdt-radius-75);
    font-family: inherit;
    font-size: var(--pdt-text-body-sm-font-size);
    line-height: var(--pdt-text-body-sm-line-height);
    color: var(--pdt-color-text);

    &:focus-visible {
      border-color: var(--pdt-color-border-focused);
      box-shadow: var(--pdt-shadow-focus);
    }

    &::placeholder {
      color: var(--pdt-color-text-disabled);
    }
  }
}
</style>
